import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DocumentTemplateService } from '../../services/document-template/document-template.service';

import { ToastrService } from 'ngx-toastr';
import { KeyValuePair } from '../../../shared/models/key-value-pair';
import { catchError, of } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-template-share-dialog',
  templateUrl: './template-share-dialog.component.html',
  styleUrls: ['./template-share-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TemplateShareDialogComponent implements OnInit {
  private static readonly MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
  
  templateOptions: KeyValuePair[] = [];

  mode: 'select' | 'export' | 'import' = 'select';
  shareForm: FormGroup;
  isLoading = false;
  exportedData = '';
  selectedTemplateId: number | null = null;
  selectedTemplateName = '';
  importFile: File | null = null;
  importFiles: File[] = [];
  importFileNames: { [filename: string]: string } = {};

  constructor(
    private fb: FormBuilder,
    private documentTemplateService: DocumentTemplateService,
    private toastr: ToastrService,
    public activeModal: NgbActiveModal,
    private cdr: ChangeDetectorRef
  ) {
    this.shareForm = this.fb.group({
      selectedTemplate: ['', Validators.required],
      newTemplateName: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.mode = 'select';
    this.loadTemplates();
  }

  isNameDuplicate(name: string, excludeFilename?: string): boolean {
    if (!name) return false;
    const lowerName = name.trim().toLowerCase();
    
    if (this.templateOptions.some(t => t.value.toLowerCase() === lowerName)) {
      return true;
    }
    
    const duplicateInBatch = this.importFiles.some(file => {
      if (excludeFilename && file.name === excludeFilename) return false;
      const otherName = this.importFileNames[file.name];
      return otherName && otherName.trim().toLowerCase() === lowerName;
    });
    
    return duplicateInBatch;
  }

  hasAnyDuplicateNames(): boolean {
    return this.importFiles.some(file => {
      const name = this.importFileNames[file.name];
      return this.isNameDuplicate(name, file.name);
    });
  }

  hasAnyEmptyNames(): boolean {
    return this.importFiles.some(file => {
      const name = this.importFileNames[file.name];
      return !name || !name.trim();
    });
  }

  canImport(): boolean {
    return this.importFiles.length > 0 && !this.hasAnyDuplicateNames() && !this.hasAnyEmptyNames() && !this.isLoading;
  }

  private loadTemplates() {
    this.isLoading = true;
    
    this.documentTemplateService.getTemplates()
      .pipe(
        catchError((error) => {
          console.warn('Templates loading returned error (this is normal for accounts with no templates):', error);
          return of([]);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (templates) => {
          const templateList = templates || [];
          this.templateOptions = templateList.map(template => ({
            key: template.id?.toString() || '0',
            value: template.name || 'Unnamed Template'
          }));
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.templateOptions = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  selectExport() {
    const templateId = this.shareForm.get('selectedTemplate')?.value;
    const template = this.templateOptions.find(t => t.key === templateId);
    
    if (templateId && template) {
      this.selectedTemplateId = templateId;
      this.selectedTemplateName = template.value;
      this.exportTemplate();
    } else {
      this.toastr.error('Please select a template to export');
    }
  }

  selectImport() {
    this.mode = 'import';
    this.importFile = null;
    this.importFiles = [];
    this.importFileNames = {};
  }

  backToSelect() {
    this.mode = 'select';
    this.exportedData = '';
    this.importFile = null;
    this.importFiles = [];
    this.importFileNames = {};
  }

  exportTemplate() {
    if (!this.selectedTemplateId) {
      return;
    }

    this.isLoading = true;
    this.documentTemplateService.exportTemplateAsJson(this.selectedTemplateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (jsonData) => {
          this.exportedData = jsonData;
          this.isLoading = false;
          this.cdr.detectChanges();
          this.downloadTemplateFile();
        },
        error: () => {
          this.toastr.error('Failed to export template');
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  downloadTemplateFile() {
    if (!this.exportedData) {
      this.toastr.error('No template data available for download');
      return;
    }

    const fileName = `${this.selectedTemplateName}.json`;
    if (this.downloadJsonFile(this.exportedData, fileName)) {
      this.toastr.success('Template file downloaded.');
    } else {
      this.toastr.error('Failed to download template file');
    }
  }

  private downloadJsonFile(data: string, fileName: string): boolean {
    try {
      const blob = new Blob([data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      window.URL.revokeObjectURL(url);
      return true;
    } catch {
      return false;
    }
  }

  exportAllTemplates() {
    if (this.templateOptions.length === 0) {
      this.toastr.error('No templates available to export');
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();

    let exportedCount = 0;
    let errorCount = 0;
    const totalTemplates = this.templateOptions.length;

    const exportNext = (index: number) => {
      if (index >= totalTemplates) {
        this.isLoading = false;
        this.cdr.detectChanges();
        if (exportedCount > 0) {
          this.toastr.success(`Exported ${exportedCount} template(s) successfully.`);
        }
        if (errorCount > 0) {
          this.toastr.warning(`Failed to export ${errorCount} template(s).`);
        }
        return;
      }

      const template = this.templateOptions[index];
      const templateId = parseInt(template.key, 10);
      const templateName = template.value;

      this.documentTemplateService.exportTemplateAsJson(templateId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (jsonData) => {
            const fileName = `${templateName}.json`;
            if (this.downloadJsonFile(jsonData, fileName)) {
              exportedCount++;
            } else {
              errorCount++;
            }
            exportNext(index + 1);
          },
          error: () => {
            errorCount++;
            exportNext(index + 1);
          }
        });
    };

    exportNext(0);
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    
    if (!files || files.length === 0) {
      return;
    }

    const newNames = { ...this.importFileNames };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        const alreadyAdded = this.importFiles.some(f => f.name === file.name);
        if (!alreadyAdded) {
          this.importFiles.push(file);
          newNames[file.name] = this.getTemplateNameFromFile(file);
        }
      }
    }

    this.importFileNames = newNames;

    if (this.importFiles.length === 0) {
      this.toastr.error('Please select valid JSON file(s)');
    } else if (this.importFiles.length === 1) {
      this.importFile = this.importFiles[0];
    } else {
      this.importFile = null;
    }

    event.target.value = '';
    
    this.cdr.detectChanges();
  }

  removeImportFile(index: number) {
    const file = this.importFiles[index];
    delete this.importFileNames[file.name];
    this.importFiles.splice(index, 1);
    if (this.importFiles.length === 1) {
      this.importFile = this.importFiles[0];
    } else {
      this.importFile = null;
    }
    this.cdr.detectChanges();
  }

  getTemplateNameFromFile(file: File): string {
    return file.name.replace(/\.json$/i, '');
  }

  updateImportFileName(filename: string, newName: string) {
    this.importFileNames[filename] = newName;
  }

  importTemplate() {
    if (this.importFiles.length > 1) {
      this.importMultipleTemplates();
      return;
    }

    if (!this.importFile && this.importFiles.length === 1) {
      this.importFile = this.importFiles[0];
    }

    if (!this.importFile) {
      this.toastr.error('Please select a template file');
      return;
    }

    const newTemplateName = this.importFileNames[this.importFile.name]?.trim();
    if (!newTemplateName) {
      this.toastr.error('Please enter a template name');
      return;
    }

    if (this.importFile.size > TemplateShareDialogComponent.MAX_FILE_SIZE_BYTES) {
      this.toastr.error('File size too large. Please select a file smaller than 10MB.');
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();
    
    const reader = new FileReader();
    
    reader.onerror = () => {
      this.toastr.error('Failed to read the selected file');
      this.isLoading = false;
      this.cdr.detectChanges();
    };
    
    reader.onload = (e) => {
      try {
        const templateData = e.target?.result as string;
        
        if (!templateData || templateData.trim().length === 0) {
          this.toastr.error('Selected file appears to be empty');
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.documentTemplateService.importTemplateFromJson(newTemplateName, templateData)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (result) => {
              this.toastr.success(`Template "${newTemplateName}" imported successfully.`);
              this.activeModal.close(result);
            },
            error: () => {
              this.toastr.error('Failed to import template. Please check the template data format and that the template name is unique.');
              this.isLoading = false;
              this.cdr.detectChanges();
            }
          });
      } catch (error) {
        this.toastr.error('Invalid template file format. Please select a valid JSON file.');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    };

    reader.readAsText(this.importFile);
  }

  private importMultipleTemplates() {
    const oversizedFiles = this.importFiles.filter(f => f.size > TemplateShareDialogComponent.MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      this.toastr.error(`${oversizedFiles.length} file(s) are too large. Please select files smaller than 10MB.`);
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();

    let successCount = 0;
    let errorCount = 0;
    const totalFiles = this.importFiles.length;

    const processNextFile = (index: number) => {
      if (index >= totalFiles) {
        this.isLoading = false;
        if (successCount > 0) {
          this.toastr.success(`Successfully imported ${successCount} template(s).`);
          if (errorCount > 0) {
            this.toastr.warning(`Failed to import ${errorCount} template(s).`);
          }
          this.activeModal.close({ imported: successCount });
        } else {
          this.toastr.error('Failed to import any templates.');
          this.cdr.detectChanges();
        }
        return;
      }

      const file = this.importFiles[index];
      const templateName = this.importFileNames[file.name] || this.getTemplateNameFromFile(file);
      const reader = new FileReader();

      reader.onerror = () => {
        errorCount++;
        processNextFile(index + 1);
      };

      reader.onload = (e) => {
        try {
          const templateData = e.target?.result as string;
          
          if (!templateData || templateData.trim().length === 0) {
            errorCount++;
            processNextFile(index + 1);
            return;
          }

          this.documentTemplateService.importTemplateFromJson(templateName, templateData)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                successCount++;
                processNextFile(index + 1);
              },
              error: () => {
                errorCount++;
                processNextFile(index + 1);
              }
            });
        } catch {
          errorCount++;
          processNextFile(index + 1);
        }
      };

      reader.readAsText(file);
    };

    processNextFile(0);
  }

  onCancel() {
    this.activeModal.dismiss();
  }
}